// ── Doqubox Crypto Module ───────────────────────────────────────────
//
// Centralised cryptographic helpers used across Stimulus controllers.
// All functions are pure ES module exports — no window globals needed.
//
// Key hierarchy:
//   UEK  – ECDH P-256 per-user (private wrapped with PRF-derived AES key)
//   TEK  – ECDH P-256 per-company (public on server, private wrapped per-user)
//   FEK  – Ephemeral AES-256-GCM per-file (derived via ECIES for inbox)
//   MK   – Random AES-256-GCM per-message (for outbox)
//

const textEncoder = new TextEncoder()
const ECIES_V1_VERSION = 1
const ECIES_SALT_BYTES = 16
const FILE_ENCRYPTION_INFO_V1 = "doqubox/file-encrypt/v1"
const ECIES_INFO_V1 = "doqubox/ecies-wrap/v1"

// ── Base64url ↔ ArrayBuffer ─────────────────────────────────────────

export function base64UrlToArrayBuffer(value) {
  const buffer = bufferSourceToArrayBuffer(value)
  if (buffer) return buffer

  if (typeof value !== "string") {
    throw new Error("Invalid base64url value")
  }

  const padding = "=".repeat((4 - (value.length % 4)) % 4)
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/")

  try {
    const binary = atob(base64)
    const buffer = new ArrayBuffer(binary.length)
    const bytes = new Uint8Array(buffer)

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }

    return buffer
  } catch (error) {
    throw new Error("Invalid base64url encoding")
  }
}

export function normalizeBufferSource(value, label = "Key data") {
  const buffer = bufferSourceToArrayBuffer(value)
  if (buffer) return buffer

  if (typeof value === "string") {
    return base64UrlToArrayBuffer(value)
  }

  throw new Error(`${label} must be binary data`)
}

function bufferSourceToArrayBuffer(value) {
  if (!value || typeof value !== "object") return null

  if (Array.isArray(value)) {
    if (value.every((byte) => Number.isInteger(byte) && byte >= 0 && byte <= 255)) {
      return Uint8Array.from(value).buffer
    }

    return null
  }

  if (ArrayBuffer.isView(value)) {
    return value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength)
  }

  if (typeof value.byteLength === "number" && typeof value.slice === "function") {
    try {
      return value.slice(0)
    } catch (error) {
      return null
    }
  }

  return null
}

export function arrayBufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ""

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

// ── WebAuthn credential encoding/decoding ───────────────────────────

export function decodePublicKeyCredentialCreationOptions(options) {
  return {
    ...options,
    challenge: base64UrlToArrayBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64UrlToArrayBuffer(options.user.id)
    },
    excludeCredentials: (options.excludeCredentials || []).map((credential) => ({
      ...credential,
      id: base64UrlToArrayBuffer(credential.id)
    }))
  }
}

export function decodePublicKeyCredentialRequestOptions(options) {
  const decoded = {
    ...options,
    challenge: base64UrlToArrayBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((credential) => ({
      ...credential,
      id: base64UrlToArrayBuffer(normalizeCredentialId(credential.id))
    }))
  }

  if (options.extensions?.prf?.eval?.first) {
    decoded.extensions = {
      ...options.extensions,
      prf: {
        ...options.extensions.prf,
        eval: {
          ...options.extensions.prf.eval,
          first: base64UrlToArrayBuffer(options.extensions.prf.eval.first)
        }
      }
    }
  }

  return decoded
}

function normalizeCredentialId(value) {
  if (value && typeof value === "object" && value.id) {
    return value.id
  }

  return value
}

export function encodePublicKeyCredential(credential) {
  return {
    id: credential.id,
    type: credential.type,
    rawId: arrayBufferToBase64Url(credential.rawId),
    response: {
      clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
      attestationObject: credential.response.attestationObject ? arrayBufferToBase64Url(credential.response.attestationObject) : undefined,
      authenticatorData: credential.response.authenticatorData ? arrayBufferToBase64Url(credential.response.authenticatorData) : undefined,
      signature: credential.response.signature ? arrayBufferToBase64Url(credential.response.signature) : undefined,
      userHandle: credential.response.userHandle ? arrayBufferToBase64Url(credential.response.userHandle) : undefined
    }
  }
}

// ── UEK (User Encryption Key) ───────────────────────────────────────

export async function generateAndWrapUek(prfResult) {
  const prfBytes = normalizeBufferSource(prfResult, "Passkey PRF result")
  const uekKeyPair = await window.crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveKey", "deriveBits"]
  )

  const publicKeyDer = await window.crypto.subtle.exportKey("spki", uekKeyPair.publicKey)
  const privateKeyDer = await window.crypto.subtle.exportKey("pkcs8", uekKeyPair.privateKey)

  const prfKey = await window.crypto.subtle.importKey(
    "raw",
    prfBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  )

  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    prfKey,
    privateKeyDer
  )

  const combined = new Uint8Array(iv.byteLength + ciphertext.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(ciphertext), iv.byteLength)

  return {
    uekKeyPair,
    uekPrivateKeyDer: privateKeyDer,
    publicKeyDer: arrayBufferToBase64Url(publicKeyDer),
    wrappedPrivateKey: arrayBufferToBase64Url(combined.buffer)
  }
}

// ── TEK (Team Encryption Key) ───────────────────────────────────────

// ECIES wrap: encrypt data with an ECDH public key
// Returns { combined: Uint8Array } in format:
//   v1:
//     version (1 byte) + ephemeral public key length (2 bytes) + ephemeral public
//     key + hkdf salt (16 bytes) + iv + ciphertext
async function eciesEncrypt(publicKey, plaintext) {
  const ephemeralKeyPair = await window.crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits"]
  )

  const sharedBits = await window.crypto.subtle.deriveBits(
    { name: "ECDH", public: publicKey },
    ephemeralKeyPair.privateKey,
    256
  )
  const salt = window.crypto.getRandomValues(new Uint8Array(ECIES_SALT_BYTES))
  const wrappingKey = await deriveAesGcmKeyWithHkdf(
    sharedBits,
    salt,
    ECIES_INFO_V1,
    ["encrypt"]
  )

  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    wrappingKey,
    plaintext
  )

  const ephemeralPublicKeyDer = await window.crypto.subtle.exportKey("spki", ephemeralKeyPair.publicKey)
  const ephemeralPublicKeyBytes = new Uint8Array(ephemeralPublicKeyDer)
  const combined = new Uint8Array(1 + 2 + ephemeralPublicKeyBytes.byteLength + salt.byteLength + iv.byteLength + ciphertext.byteLength)
  const view = new DataView(combined.buffer, combined.byteOffset, combined.byteLength)
  combined[0] = ECIES_V1_VERSION
  view.setUint16(1, ephemeralPublicKeyBytes.byteLength, false)
  combined.set(ephemeralPublicKeyBytes, 3)
  combined.set(salt, 3 + ephemeralPublicKeyBytes.byteLength)
  combined.set(iv, 3 + ephemeralPublicKeyBytes.byteLength + salt.byteLength)
  combined.set(new Uint8Array(ciphertext), 3 + ephemeralPublicKeyBytes.byteLength + salt.byteLength + iv.byteLength)

  return combined
}

// ECIES decrypt: decrypt data that was encrypted with eciesEncrypt
// combined: Uint8Array in the same format
async function eciesDecrypt(privateKey, combined) {
  if (combined[0] !== ECIES_V1_VERSION) {
    throw new Error("Unsupported encrypted key format")
  }

  const view = new DataView(combined.buffer, combined.byteOffset, combined.byteLength)
  const ephemeralPublicKeyLength = view.getUint16(1, false)
  const offset = 3
  const ephemeralPublicKeyDer = combined.slice(offset, offset + ephemeralPublicKeyLength)
  const salt = combined.slice(offset + ephemeralPublicKeyLength, offset + ephemeralPublicKeyLength + ECIES_SALT_BYTES)
  const iv = combined.slice(offset + ephemeralPublicKeyLength + ECIES_SALT_BYTES, offset + ephemeralPublicKeyLength + ECIES_SALT_BYTES + 12)
  const ciphertext = combined.slice(offset + ephemeralPublicKeyLength + ECIES_SALT_BYTES + 12)

  const ephemeralPublicKey = await window.crypto.subtle.importKey(
    "spki",
    ephemeralPublicKeyDer,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  )

  const sharedBits = await window.crypto.subtle.deriveBits(
    { name: "ECDH", public: ephemeralPublicKey },
    privateKey,
    256
  )

  const unwrappingKey = await deriveAesGcmKeyWithHkdf(
    sharedBits,
    salt,
    ECIES_INFO_V1,
    ["decrypt"]
  )

  return await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    unwrappingKey,
    ciphertext
  )
}

async function deriveAesGcmKeyWithHkdf(sharedBits, salt, infoLabel, usages) {
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    sharedBits,
    "HKDF",
    false,
    ["deriveKey"]
  )

  return await window.crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt,
      info: textEncoder.encode(infoLabel)
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    usages
  )
}

// Generate a new TEK and wrap the private key with the UEK public key (ECIES)
export async function generateAndWrapTek(uekPublicKey) {
  const tekKeyPair = await window.crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits"]
  )

  const tekPrivateKeyDer = await window.crypto.subtle.exportKey("pkcs8", tekKeyPair.privateKey)
  const tekPublicKeyDer = await window.crypto.subtle.exportKey("spki", tekKeyPair.publicKey)

  const combined = await eciesEncrypt(uekPublicKey, tekPrivateKeyDer)

  return {
    tekWrapped: arrayBufferToBase64Url(combined.buffer),
    tekPublicKey: arrayBufferToBase64Url(tekPublicKeyDer)
  }
}

// Generate a new TEK, wrap it, and also return the raw private key for session
export async function generateAndWrapTekWithRaw(uekPublicKey) {
  const tekKeyPair = await window.crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits"]
  )

  const tekPrivateKeyDer = await window.crypto.subtle.exportKey("pkcs8", tekKeyPair.privateKey)
  const tekPublicKeyDer = await window.crypto.subtle.exportKey("spki", tekKeyPair.publicKey)

  const combined = await eciesEncrypt(uekPublicKey, tekPrivateKeyDer)

  return {
    tekWrapped: arrayBufferToBase64Url(combined.buffer),
    tekPublicKey: arrayBufferToBase64Url(tekPublicKeyDer),
    tekRaw: tekPrivateKeyDer
  }
}

// Unwrap TEK private key using UEK private key (ECIES decrypt)
export async function unwrapTekWithUek(tekWrapped, uekPrivateKey) {
  const combined = new Uint8Array(base64UrlToArrayBuffer(tekWrapped))
  return await eciesDecrypt(uekPrivateKey, combined)
}

// Wrap a raw TEK with a UEK public key (ECIES encrypt)
export async function wrapTekWithUek(tekRaw, uekPublicKey) {
  const combined = await eciesEncrypt(uekPublicKey, tekRaw)
  return arrayBufferToBase64Url(combined.buffer)
}

// Wrap TEK with a one-time key (OTK) for invitations
export async function wrapTekWithOtk(tekWrapped, uekPrivateKey, otk) {
  const tekRaw = await unwrapTekWithUek(tekWrapped, uekPrivateKey)

  const otkKey = await window.crypto.subtle.importKey(
    "raw",
    base64UrlToArrayBuffer(otk),
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  )

  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    otkKey,
    tekRaw
  )

  const combined = new Uint8Array(iv.byteLength + ciphertext.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(ciphertext), iv.byteLength)

  return arrayBufferToBase64Url(combined.buffer)
}

// Rewrap TEK from invitation (OTK-wrapped) to user's UEK
export async function rewrapTekWithUek(tekWrappedWithOtk, otk, uekPublicKey) {
  const tekPrivateKeyDer = await unwrapOtk(tekWrappedWithOtk, otk)
  const combined = await eciesEncrypt(uekPublicKey, tekPrivateKeyDer)
  return arrayBufferToBase64Url(combined.buffer)
}

// Rewrap TEK from invitation and also return the raw TEK for session storage
export async function rewrapTekWithUekAndGetRaw(tekWrappedWithOtk, otk, uekPublicKey) {
  const tekPrivateKeyDer = await unwrapOtk(tekWrappedWithOtk, otk)
  const combined = await eciesEncrypt(uekPublicKey, tekPrivateKeyDer)
  return {
    tekWrapped: arrayBufferToBase64Url(combined.buffer),
    tekRaw: tekPrivateKeyDer
  }
}

// ── OTK (One-Time Key) helpers ──────────────────────────────────────

async function unwrapOtk(tekWrappedWithOtk, otk) {
  const combined = new Uint8Array(base64UrlToArrayBuffer(tekWrappedWithOtk))
  const iv = combined.slice(0, 12)
  const ciphertext = combined.slice(12)

  const otkKey = await window.crypto.subtle.importKey(
    "raw",
    base64UrlToArrayBuffer(otk),
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  )

  return await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    otkKey,
    ciphertext
  )
}

export function generateOtk() {
  const otk = window.crypto.getRandomValues(new Uint8Array(32))
  return arrayBufferToBase64Url(otk.buffer)
}

export function getOtkFromFragment() {
  const hash = window.location.hash
  if (hash && hash.length > 1) {
    return hash.substring(1)
  }
  return null
}

// ── Key store (IndexedDB + non-extractable CryptoKey) ───────────────
//
// The UEK private key is stored as a non-extractable CryptoKey in IndexedDB.
// This prevents XSS from exfiltrating the raw key material — an attacker can
// use the key during an active session but cannot steal it for offline use.
// The wrapped TEK (an ECIES ciphertext string) is stored alongside it.

const DB_NAME = "doqubox"
const DB_VERSION = 1
const STORE_NAME = "keystore"

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function idbGet(key) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly")
    const req = tx.objectStore(STORE_NAME).get(key)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
    tx.oncomplete = () => db.close()
  })
}

async function idbPut(key, value) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite")
    tx.objectStore(STORE_NAME).put(value, key)
    tx.oncomplete = () => { db.close(); resolve() }
    tx.onerror = () => { db.close(); reject(tx.error) }
  })
}

async function idbClear() {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite")
    tx.objectStore(STORE_NAME).clear()
    tx.oncomplete = () => { db.close(); resolve() }
    tx.onerror = () => { db.close(); reject(tx.error) }
  })
}

export async function hasTekInSession() {
  const [uek, tekWrapped] = await Promise.all([idbGet("uek"), idbGet("tek_wrapped")])
  return uek != null && tekWrapped != null
}

export async function clearKeyStore() {
  await idbClear()
}

// Store UEK private key as a non-extractable CryptoKey in IndexedDB.
// Accepts raw PKCS8 DER bytes (ArrayBuffer) — the import happens here.
export async function storeUek(privateKeyDer) {
  const privateKeyBytes = normalizeBufferSource(privateKeyDer, "UEK private key")
  const cryptoKey = await window.crypto.subtle.importKey(
    "pkcs8",
    privateKeyBytes,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    ["deriveBits"]
  )
  await idbPut("uek", cryptoKey)
}

export async function storeTekWrapped(tekWrapped) {
  await idbPut("tek_wrapped", tekWrapped)
}

// Get UEK private key CryptoKey directly from IndexedDB (already non-extractable)
async function importUekPrivateKey() {
  return await idbGet("uek") || null
}

// Get TEK private key from IndexedDB (unwraps on-demand using stored UEK)
export async function getTekFromSession() {
  const tekWrapped = await idbGet("tek_wrapped")
  if (!tekWrapped) return null

  const uekPrivateKey = await importUekPrivateKey()
  if (!uekPrivateKey) return null

  return await unwrapTekWithUek(tekWrapped, uekPrivateKey)
}

// Import TEK private key from session as CryptoKey (ECDH P-256)
export async function importTekPrivateKeyFromSession() {
  const tekPrivateKeyDer = await getTekFromSession()
  if (!tekPrivateKeyDer) {
    throw new Error("TEK niet beschikbaar. Log opnieuw in.")
  }

  return await window.crypto.subtle.importKey(
    "pkcs8",
    tekPrivateKeyDer,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    ["deriveBits"]
  )
}

// ── File encryption (inbox — ECIES per file) ────────────────────────

// Encrypt a file using ECIES with the TEK public key
export async function encryptFileWithTekPublicKey(tekPublicKeyBase64, data) {
  const tekPublicKey = await window.crypto.subtle.importKey(
    "spki",
    base64UrlToArrayBuffer(tekPublicKeyBase64),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  )

  const ephemeralKeyPair = await window.crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits"]
  )

  const sharedBits = await window.crypto.subtle.deriveBits(
    { name: "ECDH", public: tekPublicKey },
    ephemeralKeyPair.privateKey,
    256
  )
  const salt = window.crypto.getRandomValues(new Uint8Array(ECIES_SALT_BYTES))
  const fek = await deriveAesGcmKeyWithHkdf(
    sharedBits,
    salt,
    FILE_ENCRYPTION_INFO_V1,
    ["encrypt"]
  )

  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    fek,
    data
  )

  const ephemeralPublicKeyDer = await window.crypto.subtle.exportKey("spki", ephemeralKeyPair.publicKey)

  return {
    ciphertext: ciphertext,
    ephemeralPublicKey: arrayBufferToBase64Url(ephemeralPublicKeyDer),
    iv: arrayBufferToBase64Url(iv.buffer),
    salt: arrayBufferToBase64Url(salt.buffer)
  }
}

// Decrypt a file using ECIES with the TEK private key
export async function decryptFileWithTekPrivateKey(ephemeralPublicKeyBase64, ivBase64, ciphertext, saltBase64) {
  if (!saltBase64) {
    throw new Error("Missing encryption salt")
  }

  const tekPrivateKey = await importTekPrivateKeyFromSession()

  const ephemeralPublicKey = await window.crypto.subtle.importKey(
    "spki",
    base64UrlToArrayBuffer(ephemeralPublicKeyBase64),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  )

  const sharedBits = await window.crypto.subtle.deriveBits(
    { name: "ECDH", public: ephemeralPublicKey },
    tekPrivateKey,
    256
  )
  const fek = await deriveAesGcmKeyWithHkdf(
    sharedBits,
    new Uint8Array(base64UrlToArrayBuffer(saltBase64)),
    FILE_ENCRYPTION_INFO_V1,
    ["decrypt"]
  )

  const iv = new Uint8Array(base64UrlToArrayBuffer(ivBase64))
  return await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    fek,
    ciphertext
  )
}

// ── Message key helpers (outbox — AES-256-GCM per message) ──────────

export async function generateMessageKey() {
  return await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  )
}

export async function encryptFileWithMessageKey(messageKey, data) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    messageKey,
    data
  )
  return {
    ciphertext,
    iv: arrayBufferToBase64Url(iv.buffer)
  }
}

export async function decryptFileWithMessageKey(messageKey, ivBase64, ciphertext) {
  const iv = new Uint8Array(base64UrlToArrayBuffer(ivBase64))
  return await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    messageKey,
    ciphertext
  )
}

// Wrap a message key with the TEK public key (ECIES)
export async function wrapMessageKeyWithTek(tekPublicKeyBase64, messageKey) {
  const rawKey = await window.crypto.subtle.exportKey("raw", messageKey)

  const tekPublicKey = await window.crypto.subtle.importKey(
    "spki",
    base64UrlToArrayBuffer(tekPublicKeyBase64),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  )

  const combined = await eciesEncrypt(tekPublicKey, rawKey)
  return arrayBufferToBase64Url(combined.buffer)
}

// Unwrap a message key using the TEK private key (ECIES)
export async function unwrapMessageKeyWithTek(encryptedMessageKey) {
  const tekPrivateKey = await importTekPrivateKeyFromSession()
  const combined = new Uint8Array(base64UrlToArrayBuffer(encryptedMessageKey))
  const rawKey = await eciesDecrypt(tekPrivateKey, combined)

  return await window.crypto.subtle.importKey(
    "raw",
    rawKey,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  )
}

export async function exportMessageKey(messageKey) {
  const rawKey = await window.crypto.subtle.exportKey("raw", messageKey)
  return arrayBufferToBase64Url(rawKey)
}

export async function importMessageKey(base64urlKey) {
  const rawKey = base64UrlToArrayBuffer(base64urlKey)
  return await window.crypto.subtle.importKey(
    "raw",
    rawKey,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  )
}
